import { Category } from '@modules/husleie-framework/src/enums/Category.enum';
import { UserAppellationKey } from '@modules/husleie-framework/src/enums/UserAppellation.enum';
import { UserTitleKey } from '@modules/husleie-framework/src/enums/UserTitle.enum';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import { RentalObjectFurnishingStandardEnum } from '@modules/husleie-framework/src/types/RentalObject.entity';
import { Session, SessionTypeEnum } from '@modules/husleie-framework/src/types/Session.entity';
import { SigningStatusEnum } from '@modules/husleie-framework/src/types/Signing.entity';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';
import { UtilityTypeEnum, UtilityStatusEnum } from '@modules/husleie-framework/src/types/Utility';
import SessionConversionOperations from '@modules/store/session/static-classes/SessionConversionOperations';
import type {
  SessionAPIInterface,
  SessionCreate,
  SessionCreateResponse,
  SessionClone,
  SessionCloneResponse,
  SessionGet,
  SessionGetResponse,
  SessionPreview,
  SessionPreviewResponse,
  SessionUpdate,
  SessionUpdateResponse,
  SessionDelete,
  SessionSigningMethodsGet,
  SessionSigningMethodsGetResponse,
} from '@modules/store/session/types/SessionAPI.interface';
import { SessionSigningPrepare, SessionSigningPrepareResponse } from '@modules/store/session/types/SessionAPI.interface';

export default class API implements SessionAPIInterface {
  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getSession(get: SessionGet): Promise<SessionGetResponse> {
    const data: Session = {
      uuid: '3fdf4ce8-f229-4fa1-817e-e31c4e0ca127',
      type: get.type, // 'MOVE_IN'
      status: SigningStatusEnum.SIGNING_TENANT,
      landlord: {
        type: LandlordTypeEnum.PRIVATE,
        id: 37,
        user: {
          firstName: 'Example',
          lastName: 'Landlord',
          fullName: 'Example Landlord',
          email: 'example@landlord.com',
          role: UserRoleEnum.LANDLORD,
          appellation: UserAppellationKey.MSS,
        },
      },
      tenant: {
        user: {
          id: 10,
          firstName: 'Milli',
          lastName: 'Vanilli',
          fullName: 'Milli Vanilli',
          email: 'milli.vanilli@hotshots.com',
          appellation: UserAppellationKey.MR, // 'Mister'
          title: UserTitleKey.PROF_DR, // 'Prof. Dr.'
          role: UserRoleEnum.TENANT, // 'TENANT'
        },
        address: {
          street: 'Street 1',
          additionalAddress: '',
          postalCode: '11111',
          postalArea: 'M City',
          country: {
            code: 'DE',
          },
        },
      },
      coTenants: [],
      rentalObject: {
        category: Category.APARTMENT, // 'APARTMENT'
        address: {
          street: 'Street 2',
          additionalAddress: '',
          apartmentLocation: 'second floor',
          postalCode: '11111',
          postalArea: 'M City',
          country: {
            code: 'DE',
          },
        },
        furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
        rooms: [
          {
            name: 'Kitchen',
            images: [
              {
                token: 'token1',
                imageSrc: 'http://placekitten.com/201/100',
              },
              {
                token: 'token2',
                imageSrc: 'http://placekitten.com/200/201',
              },
              {
                token: 'token3',
                imageSrc: 'http://placekitten.com/200/202',
              },
              {
                token: 'token4',
                imageSrc: 'http://placekitten.com/200/203',
              },
              {
                token: 'token5',
                imageSrc: 'http://placekitten.com/200/204',
              },
            ],
          },
          {
            name: 'Living Room',
            images: [
              {
                token: 'token6',
                imageSrc: 'http://placekitten.com/201/100',
              },
              {
                token: 'token7',
                imageSrc: 'http://placekitten.com/200/201',
              },
              {
                token: 'token8',
                imageSrc: 'http://placekitten.com/200/202',
              },
              {
                token: 'token9',
                imageSrc: 'http://placekitten.com/200/203',
              },
              {
                token: 'token10',
                imageSrc: 'http://placekitten.com/200/204',
              },
            ],
          },
        ],
        keychain: {
          keys: [
            {
              name: 'Front door',
              quantity: 2,
            },
            {
              name: 'Back door',
              quantity: 2,
            },
          ],
        },
        utilities: [
          {
            type: UtilityTypeEnum.ELECTRICITY,
            state: UtilityStatusEnum.RELEVANT,
            meter: {
              number: 'si39wjsjh3783',
              reading: {
                date: '10.12.2021',
                value: 8833,
                images: [
                  {
                    token: 'token11',
                    imageSrc: 'http://placekitten.com/201/100',
                  },
                ],
                comment: '',
              },
            },
          },
          {
            type: UtilityTypeEnum.COLD_WATER,
            state: UtilityStatusEnum.RELEVANT,
            meter: {
              reading: {
                date: '10.12.2021',
                images: [
                  {
                    token: 'token12',
                    imageSrc: 'http://placekitten.com/201/100',
                  },
                ],
                comment: '',
              },
            },
          },
          {
            type: UtilityTypeEnum.HOT_WATER,
            state: UtilityStatusEnum.NOT_RELEVANT,
            meter: {
              reading: {
                images: [],
              },
            },
          },
        ],
        details: {
          amountOfResidents: 1,
          furnishingStandard: RentalObjectFurnishingStandardEnum.UNFURNISHED,
          moveInDate: '10.12.2021',
        },
      },
      comment: '',
      images: [],
    };

    LogOperations.debug('API|MOCK session::GET response', data);

    return new Promise(resolve => resolve({ session: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  createSession(create: SessionCreate): Promise<SessionCreateResponse> {
    const data: Session = {
      uuid: '3fdf4ce8-f229-4fa1-817e-e31c4e0ca127',
      type: create.type,
      status: SigningStatusEnum.SIGNING_LANDLORD,
      landlord: {
        type: LandlordTypeEnum.PRIVATE,
        id: 37,
        user: {
          firstName: 'Example',
          lastName: 'Landlord',
          fullName: 'Example Landlord',
          email: 'example@landlord.com',
          role: UserRoleEnum.LANDLORD,
          appellation: UserAppellationKey.MSS,
        },
      },
      tenant: {
        user: {
          id: 10,
          firstName: 'Milli',
          lastName: 'Mocatti',
          fullName: 'Milli Mocatti',
          appellation: UserAppellationKey.MSS,
          title: UserTitleKey.PROF_DR,
          role: UserRoleEnum.TENANT,
        },
      },
      coTenants: [],
      rentalObject: {
        category: Category.APARTMENT,
        furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
        rooms: [],
        keychain: {
          keys: [
            {
              name: 'Front door',
              quantity: 2,
            },
            {
              name: 'Back door',
              quantity: 2,
            },
          ],
        },
        utilities: [
          {
            type: UtilityTypeEnum.ELECTRICITY,
            state: UtilityStatusEnum.RELEVANT,
            meter: {
              number: 'si39wjsjh3783',
              reading: {
                date: '10.12.2021',
                value: 8833,
                images: [
                  {
                    token: 'token1',
                    imageSrc: 'http://placekitten.com/201/100',
                  },
                ],
                comment: '',
              },
            },
          },
          {
            type: UtilityTypeEnum.COLD_WATER,
            state: UtilityStatusEnum.RELEVANT,
            meter: {
              reading: {
                date: '10.12.2021',
                images: [
                  {
                    token: 'token2',
                    imageSrc: 'http://placekitten.com/201/100',
                  },
                ],
                comment: '',
              },
            },
          },
          {
            type: UtilityTypeEnum.HOT_WATER,
            state: UtilityStatusEnum.NOT_RELEVANT,
            meter: {
              reading: {
                images: [],
              },
            },
          },
        ],
        details: {
          amountOfResidents: 1,
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          moveInDate: '01.12.2021',
        },
      },
      comment: '',
      images: [],
    };

    LogOperations.debug('API|MOCK session::POST response', data);

    return new Promise(resolve => resolve({ session: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  cloneSession(clone: SessionClone): Promise<SessionCloneResponse> {
    const data: Session = {
      uuid: '3fdf4ce8-f229-4fa1-817e-e31c4e0ca127',
      type: SessionTypeEnum.MOVE_IN,
      status: SigningStatusEnum.SIGNING_LANDLORD,
      landlord: {
        type: LandlordTypeEnum.PRIVATE,
        id: 37,
        user: {
          firstName: 'Example',
          lastName: 'Landlord',
          fullName: 'Example Landlord',
          email: 'example@landlord.com',
          role: UserRoleEnum.LANDLORD,
          appellation: UserAppellationKey.MSS,
        },
      },
      tenant: {
        user: {
          id: 10,
          firstName: 'Milli',
          lastName: 'Mocatti',
          fullName: 'Milli Mocatti',
          appellation: UserAppellationKey.MSS,
          title: UserTitleKey.PROF_DR,
          role: UserRoleEnum.TENANT,
        },
      },
      coTenants: [],
      rentalObject: {
        category: Category.APARTMENT,
        furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
        rooms: [],
        keychain: {
          keys: [
            {
              name: 'Front door',
              quantity: 2,
            },
            {
              name: 'Back door',
              quantity: 2,
            },
          ],
        },
        utilities: [
          {
            type: UtilityTypeEnum.ELECTRICITY,
            state: UtilityStatusEnum.RELEVANT,
            meter: {
              number: 'si39wjsjh3783',
              reading: {
                date: '10.12.2021',
                value: 8833,
                images: [
                  {
                    token: 'token1',
                    imageSrc: 'http://placekitten.com/201/100',
                  },
                ],
                comment: '',
              },
            },
          },
          {
            type: UtilityTypeEnum.COLD_WATER,
            state: UtilityStatusEnum.RELEVANT,
            meter: {
              reading: {
                date: '10.12.2021',
                images: [
                  {
                    token: 'token2',
                    imageSrc: 'http://placekitten.com/201/100',
                  },
                ],
                comment: '',
              },
            },
          },
          {
            type: UtilityTypeEnum.HOT_WATER,
            state: UtilityStatusEnum.NOT_RELEVANT,
            meter: {
              reading: {
                images: [],
              },
            },
          },
        ],
        details: {
          amountOfResidents: 1,
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          moveInDate: '01.12.2021',
        },
      },
      comment: '',
      images: [],
    };

    LogOperations.debug('API|MOCK session::POST clone response', data);

    return new Promise(resolve => resolve({ session: data }));
  }

  updateSession(update: SessionUpdate): Promise<SessionUpdateResponse> {
    const data: Session = update.session;
    const apiData = SessionConversionOperations.internalToApi(data);

    LogOperations.debug('API|MOCK session::POST update Session response', '\ninternal: ', data, '\napi: ', apiData);

    return new Promise(resolve => resolve({ session: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  deleteSession(del: SessionDelete): Promise<void> {
    LogOperations.debug('API|MOCK session::DELETE Session');

    return new Promise(resolve => resolve());
  }

  fetchSessionPreview(download: SessionPreview): Promise<SessionPreviewResponse> {
    const data =
      download.format === 'html'
        ? {
            html: `<h1>Mocked preview</h1><br>We are in MOCKED mode, the real preview will show here in production mode.`,
          }
        : {
            pdf: new Blob(['testing'], { type: 'application/pdf' }),
          };
    LogOperations.debug('API|MOCK session::GET preview', data);

    if (download.format === 'html') {
      return new Promise(resolve => {
        setTimeout(() => resolve(data), 2000);
      });
    } else {
      return Promise.resolve(data);
    }
  }

  getSessionSigningMethods(get: SessionSigningMethodsGet): Promise<SessionSigningMethodsGetResponse> {
    const data = { signingMethods: ['token'] };

    LogOperations.debug('API|MOCK session::GET signing method request', get);
    LogOperations.debug('API|MOCK session::GET signing method response', data);

    return Promise.resolve(data);
  }

  prepareSessionSigning(prepare: SessionSigningPrepare): Promise<SessionSigningPrepareResponse> {
    const data: SessionSigningPrepareResponse = {
      uuid: '51c3bb6f-6776-4635-aaec-be2bcc0aae93-SIGN',
      landlordSignerUuid: '51c3bb6f-6776-4635-aaec-be2bcc0aae93-SIGNER',
    };

    LogOperations.debug('API|MOCK session::POST prepare signing request', prepare);
    LogOperations.debug('API|MOCK session::POST prepare signing response', data);

    return Promise.resolve(data);
  }
}
